import { createMemo, Show, type Component } from "solid-js";
import type { RouteSectionProps } from "@solidjs/router";
import withPagePermissionCheck from "../../../../../utils/withPagePermissionCheck.tsx";
import PermissionService from "../../../../../services/PermissionService.ts";
import { useCustomerState } from "../../../../../state/customer.tsx";
import SettingsPageHeader from "../../components/SettingsPageHeader.tsx";
import BillingInformationSection from "./BillingInformationSection.tsx";
import CurrentPlanSection from "./plan/CurrentPlanSection.tsx";
import { useTranslation } from "../../../../../state/i18n.tsx";
import SeparatorLine from "../../../../../components/SeparatorLine.tsx";
import AdditionalInformationSection from "./AdditionalInformationSection.tsx";
import PaymentMethodSection from "./PaymentMethodSection.tsx";
import CouponSection from "./CouponSection.tsx";
import CancelSection from "./CancelSection.tsx";
import environment from "../../../../../environment.ts";
import UnderConstruction from "../../../../../components/UnderConstruction.tsx";
import BillingService from "../../../../../services/BillingService.ts";
import { QueryBoundary } from "../../../../../utils/QueryBoundary.tsx";
import { condition } from "../../../../../utils/solidUtils.ts";
import { isNil } from "lodash-es";
import { Icon } from "../../../../../components/icon/Icon.tsx";
import { findBaseSubscription } from "../../../../../utils/planUtil.ts";

const BillingOverviewPage: Component<RouteSectionProps<never>> = () => {
  const { t } = useTranslation();
  const customer = useCustomerState();
  const billingUsageQuery = BillingService.getBillingUsage();
  const billingProfileQuery = BillingService.getBillingProfile();
  const subscriptionStatusQuery = BillingService.getSubscriptionStatus();
  const activeSubscriptionsQuery = BillingService.getActiveSubscriptions();
  const couponQuery = BillingService.getCoupon();

  return (
    <Show
      when={!environment.isProduction() || environment.isStorybook()}
      fallback={
        <div>
          <UnderConstruction oldPlatformPath={`/customers/${customer.customerId}/billing/overview`} />
        </div>
      }
    >
      <div
        class="flex h-full flex-col"
        aria-busy={
          billingUsageQuery.isFetching ||
          billingProfileQuery.isFetching ||
          subscriptionStatusQuery.isFetching ||
          activeSubscriptionsQuery.isFetching ||
          couponQuery.isFetching
        }
      >
        <SettingsPageHeader
          title={t(($) => $.SETTINGS_SECTION_BILLING_LABEL)}
          description={t(($) => $.SETTINGS_ITEM_BILLING_AND_PAYMENT_LABEL)}
        />
        <QueryBoundary>
          <Show
            when={condition(
              {
                billingUsageQuery,
                billingProfileQuery,
                subscriptionStatusQuery,
                activeSubscriptionsQuery,
                couponQuery,
              },
              ({
                billingUsageQuery,
                billingProfileQuery,
                subscriptionStatusQuery,
                activeSubscriptionsQuery,
                couponQuery,
              }) =>
                billingUsageQuery.isFetched &&
                billingProfileQuery.isFetched &&
                subscriptionStatusQuery.isFetched &&
                activeSubscriptionsQuery.isFetched &&
                couponQuery.isFetched,
            )}
            fallback={
              <div class="flex size-full flex-col items-center justify-center">
                <Icon name="spinner" size="2xl" type="solid" class="animate-spin icon-secondary" />
                <span class="text-medium-regular text-primary">
                  {t(($) => $.SETTINGS_ITEM_BILLING_AND_PAYMENT_LOADING)}
                </span>
              </div>
            }
          >
            {(queries) => {
              const billingUsage = createMemo(() => queries().billingUsageQuery.data);
              const baseUsagePlan = createMemo(() => billingUsage()?.plans.find(({ type }) => type === "BASE"));
              const activeSubscriptions = createMemo(() => queries().activeSubscriptionsQuery.data);
              const baseSubscription = createMemo(() => findBaseSubscription(activeSubscriptions() ?? []));
              const subscriptionStatus = createMemo(() => queries().subscriptionStatusQuery.data);
              const billingProfile = createMemo(() => queries().billingProfileQuery.data);
              const discount = createMemo(() => queries().couponQuery.data ?? null);

              return (
                <Show
                  when={condition(
                    { billingUsage, activeSubscriptions, subscriptionStatus, billingProfile },
                    ({ billingUsage, activeSubscriptions, subscriptionStatus, billingProfile }) =>
                      !isNil(billingUsage) &&
                      !isNil(activeSubscriptions) &&
                      !isNil(subscriptionStatus) &&
                      !isNil(billingProfile),
                  )}
                >
                  {(data) => (
                    <div class="-mx-px flex h-full max-w-400 flex-col gap-6 overflow-y-auto px-px pt-8 pb-6">
                      <CurrentPlanSection
                        billingUsage={data().billingUsage()!}
                        subscriptionStatus={data().subscriptionStatus()!}
                        activeSubscriptions={data().activeSubscriptions()!}
                      />
                      <SeparatorLine />
                      <BillingInformationSection billingProfile={data().billingProfile()!} />
                      <SeparatorLine />
                      <AdditionalInformationSection invoiceFields={data().billingProfile()!.invoiceFields} />
                      <SeparatorLine />
                      <PaymentMethodSection />
                      <SeparatorLine />
                      <CouponSection discount={discount()} />
                      <SeparatorLine />
                      <CancelSection subscription={baseSubscription()} cycleEnd={baseUsagePlan()?.cycle.end} />
                      <SeparatorLine />
                    </div>
                  )}
                </Show>
              );
            }}
          </Show>
        </QueryBoundary>
      </div>
    </Show>
  );
};

const ProtectedBillingPage = withPagePermissionCheck(BillingOverviewPage, PermissionService.canManageBilling);

export default ProtectedBillingPage;
